import { FormProvider, useForm } from "react-hook-form";
import {
  AppBar,
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Toolbar,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import {
  Adresa,
  DatumRegistracije,
  DokumentOIskljucenju,
  ElektronickeAdrese,
  Identifikacija,
  IzjavaOdgovornosti,
  Loading,
  OperateriPlatforme,
  OpisOkolnosti,
  OstaliOperateri,
  Poruka,
  Poruke,
  PoslovniNazivPlatforme,
  PrimarnaKontaktAdresa,
  PrimarniIdentifikatorTip,
  PrirodaObvezeIzvjescivanja,
} from "../../components";
import { useEffect, useState } from "react";
import { TIP_INFO_PORUKE, VRSTA_ZAHTJEVA } from "../../enums";
import axios from "axios";
import dohvatiSelektiraneKategorije from "../../utils/dohvatiSelektiraneKategorije";
import dayjs from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";

interface FormValues {
  vrstaZahtjeva: string;
  operaterPlatforme: {
    vrstaOperatera: {
      id: number;
    };
    primarniIdentifikator: {
      vrijednost: string;
      drzavaRezidentnosti: {
        oznaka: string;
      };
      drzavaIzdavatelj: {
        oznaka: string;
        euSifra: string;
      };
    };
    naziv: string;
    ostaliIdentifikatori?: {
      vrstaIdentifikatora: {
        id?: number;
      };
      drzavaIzdavatelj: {
        oznaka: string;
      };
    }[];
    adrese: {
      drzava: {
        oznaka: string;
      };
      mjesto: string;
    }[];
    primarnaKontaktAdresa: string;
    elektronickeAdrese?: {
      tip: {
        id?: number;
      };
      adresa: string;
    }[];
  };
  ostaliOperateri?: {
    naziv: string;
    drzava: {
      oznaka: "";
    };
    identifikator: {
      vrstaIdentifikatora: {
        id?: number;
      };
      drzavaIzdavatelj: {
        oznaka: "";
      };
    };
    adrese: {
      drzava: {
        oznaka: string;
      };
      mjesto: string;
    }[];
    elektronickeAdrese?: {
      tip: {
        id?: number;
      };
      adresa: string;
    }[];
  }[];
  poslovniNazivPlatforme: string;
  poruke: string[];
  drzave: {
    naziv: string;
    oznaka: string;
  }[];
  elektronickeAdrese: {
    id: number;
    opis: string;
  }[];
  identifikatori: {
    id: number;
    opis: string;
  }[];
  obvezeIzvjescivanja: {
    id: number;
    opis: string;
  }[];
  iskljucenje: {
    id: number;
    opis: string;
  }[];
  kategorije?: {
    id: number;
    opis: string;
  }[];
  dokumentOIskljucenju?: File;
  izjavaOOdgovornosti: Boolean;
  datumRegistracije: string | null;
  loading: Boolean;
  infoPoruka: string;
  tipInfoPoruke: number | null;
  successPoruka: string;
}

const defaultValues = {
  vrstaZahtjeva: VRSTA_ZAHTJEVA.REGISTRACIJA,
  operaterPlatforme: {
    vrstaOperatera: {
      id: -1,
    },
    primarniIdentifikator: {
      vrijednost: "",
      drzavaRezidentnosti: {
        oznaka: "HRV",
      },
      drzavaIzdavatelj: {
        oznaka: "HRV",
        euSifra: "HR",
      },
    },
    naziv: "",
    ostaliIdentifikatori: [],
    adrese: [
      {
        drzava: {
          oznaka: "",
        },
        mjesto: "",
      },
    ],
    primarnaKontaktAdresa: "",
    elektronickeAdrese: [],
  },
  ostaliOperateri: [],
  poslovniNazivPlatforme: "",
  poruke: [],
  drzave: [],
  elektronickeAdrese: [],
  identifikatori: [],
  obvezeIzvjescivanja: [],
  dokumentOIskljucenju: undefined,
  izjavaOOdgovornosti: false,
  datumRegistracije: null,
  iskljucenje: [],
  kategorije: [],
  loading: true,
  infoPoruka: "",
};

const Index = () => {
  const methods = useForm<FormValues>({ defaultValues });
  const [trenutniDatum, setTrenutniDatum] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const watchFields = methods.watch([
    "vrstaZahtjeva",
    "operaterPlatforme.vrstaOperatera.id",
    "operaterPlatforme.primarniIdentifikator.drzavaRezidentnosti",
    "operaterPlatforme.primarniIdentifikator.drzavaIzdavatelj",
    "operaterPlatforme.elektronickeAdrese",
    "drzave",
  ]);
  const successPoruka = methods.watch("successPoruka");
  const onSubmit = async (data: any) => {
    delete data.drzave;
    delete data.elektronickeAdrese;
    delete data.iskljucenje;
    delete data.obvezeIzvjescivanja;

    methods.setValue("loading", true);
    const vrstaZahtjeva = methods.getValues("vrstaZahtjeva");
    const dokumentOIskljucenju = methods.getValues("dokumentOIskljucenju");
    const kategorije = methods.getValues("kategorije");

    data.kategorije = dohvatiSelektiraneKategorije(kategorije);

    const formData = new FormData();
    formData.append(
      "request",
      new Blob([JSON.stringify(data)], {
        type: "application/json",
      })
    );
    formData.append("dokumentOIskljucenju", dokumentOIskljucenju || "");

    data = vrstaZahtjeva === VRSTA_ZAHTJEVA.REGISTRACIJA ? data : formData;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/zahtjevi/${
          VRSTA_ZAHTJEVA.REGISTRACIJA === vrstaZahtjeva
            ? "registracija"
            : "iskljucenje"
        }`,
        data,
        {
          headers: {
            "Content-Type":
              VRSTA_ZAHTJEVA.REGISTRACIJA === vrstaZahtjeva
                ? "application/json"
                : "multipart/form-data",
          },
        }
      );

      const { status } = response;
      if (status === 200) {
        methods.setValue("infoPoruka", response.data.message);
        methods.setValue("tipInfoPoruke", TIP_INFO_PORUKE.SUCCESS);
        setTrenutniDatum(null);
        methods.reset({
          ...defaultValues,
          drzave: methods.getValues("drzave"),
          elektronickeAdrese: methods.getValues("elektronickeAdrese"),
          identifikatori: methods.getValues("identifikatori"),
          obvezeIzvjescivanja: methods.getValues("obvezeIzvjescivanja"),
          iskljucenje: methods.getValues("iskljucenje"),
          infoPoruka: methods.getValues("infoPoruka"),
          tipInfoPoruke: methods.getValues("tipInfoPoruke"),
          loading: methods.getValues("loading"),
        });
      }
    } catch (error: any) {
      const { status } = error.response;
      if (status === 400) {
        if (error.response.data.errors) {
          error.response.data.errors.forEach((error: any) =>
            methods.setError(error.fieldName, { message: error.error })
          );
        }
        if (error.response.data.message) {
          methods.setValue("infoPoruka", error.response.data.message);
          methods.setValue("tipInfoPoruke", TIP_INFO_PORUKE.ERROR);
        }
      }
      if (status === 403) {
        if (error.response.data.message) {
          methods.setValue("infoPoruka", error.response.data.message);
          methods.setValue("tipInfoPoruke", TIP_INFO_PORUKE.ERROR);
        }
      }
    } finally {
      methods.setValue("loading", false);
    }
  };

  useEffect(() => {
    const dohvatiDatum = async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/datum`
      );
      const { status, data } = response;
      if (status === 200) {
        setTrenutniDatum(data);
      }
    };
    const dohvatiDrzave = async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/sifrarnici/drzave`
      );
      const { status, data } = response;
      if (status === 200) {
        methods.setValue("drzave", data);
      }
    };
    const dohvatiPoruke = async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/zahtjevi/poruka`
      );
      const { status, data } = response;
      if (status === 200) {
        methods.setValue("poruke", data);
      }
    };

    const dohvatiElektrnickeAdrese = async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/sifrarnici/elektronicke-adrese`
      );
      const { status, data } = response;
      if (status === 200) {
        methods.setValue(
          "elektronickeAdrese",
          data.filter((adresa: any) => adresa.id !== 1)
        );
      }
    };

    const dohvatiIdentifikatore = async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/sifrarnici/identifikatori`
      );
      const { status, data } = response;
      if (status === 200) {
        methods.setValue(
          "identifikatori",
          data.filter((identifikator: any) => identifikator.id === 3)
        );
      }
    };

    const dohvatiObvezeIzvjescivanja = async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/sifrarnici/obveze-izvjescivanja`
      );
      const { status, data } = response;
      if (status === 200) {
        methods.setValue("obvezeIzvjescivanja", data);
      }
    };

    const dohvatiIskljucenje = async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/sifrarnici/iskljucenje`
      );
      const { status, data } = response;
      if (status === 200) {
        const firstElement = data.shift();
        data.push(firstElement);
        methods.setValue("iskljucenje", data);
      }
    };

    const dohvatiEmail = async () => {
      try {
        const UUID = searchParams.get("UUID");
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/zahtjevi/dohvati-email/${UUID}`
        );
        const { status, data } = response;
        if (status === 200) {
          methods.setValue("operaterPlatforme.primarnaKontaktAdresa", data);
        }
      } catch (error: any) {
        navigate("/404", { replace: true });
      }
    };

    dohvatiDatum();
    // dohvatiPoruke();
    dohvatiDrzave();
    dohvatiElektrnickeAdrese();
    dohvatiIdentifikatore();
    dohvatiObvezeIzvjescivanja();
    dohvatiIskljucenje();
    dohvatiEmail();
    methods.setValue("loading", false);
  }, []);

  const onDialogClose = () => {
    if (methods.getValues("tipInfoPoruke") === TIP_INFO_PORUKE.SUCCESS) {
      methods.setValue(
        "successPoruka",
        "Vaši podaci su uspješno zaprimljeni. (Receipt of your information was successful. You can close the tab.)"
      );
    }
  };

  if (successPoruka)
    return (
      <Box>
        <Container>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: 330,
                  margin: "90px auto 0  auto",
                  textAlign: "center",
                }}
              >
                <p>{successPoruka}</p>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    );

  return (
    <FormProvider {...methods}>
      {trenutniDatum &&
        dayjs(trenutniDatum).isAfter("2023-09-30") &&
        methods.watch("operaterPlatforme.primarnaKontaktAdresa") && (
          <Box>
            <AppBar position="static" sx={{ mb: 3 }}>
              <Toolbar variant="dense">
                <Container>
                  <Typography variant="h6" color="inherit" component="div">
                    Registracija/isključivanje operatera (Registration/Exclusion
                    of operator)
                  </Typography>
                </Container>
              </Toolbar>
            </AppBar>
            <Container>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box
                    component={"form"}
                    onSubmit={(e) => {
                      methods.clearErrors();
                      methods.handleSubmit(onSubmit)(e);
                    }}
                  >
                    <Poruke />
                    <OperateriPlatforme trenutniDatum={trenutniDatum} />
                    <Identifikacija />
                    <Adresa nazivOdjeljka={"operaterPlatforme.adrese"} />
                    <PrimarnaKontaktAdresa
                      nazivPolja={"operaterPlatforme.primarnaKontaktAdresa"}
                    />
                    <ElektronickeAdrese
                      nazivOdjeljka={"operaterPlatforme.elektronickeAdrese"}
                    />
                    <PoslovniNazivPlatforme />
                    <OstaliOperateri />
                    <Grid container spacing={2}>
                      <Grid item xs={12} sx={{ mt: 3 }}>
                        <DatumRegistracije />
                      </Grid>
                      <PrirodaObvezeIzvjescivanja />
                      <OpisOkolnosti />
                      <DokumentOIskljucenju />
                      <Grid item xs={12} sx={{ mt: 3 }}>
                        <IzjavaOdgovornosti />
                      </Grid>
                    </Grid>
                    <Grid container justifyContent={"flex-end"} sx={{ mt: 3 }}>
                      <Button type={"button"}>Odustani (Cancel)</Button>
                      <Button variant={"contained"} type={"submit"}>
                        Podnesi (Submit)
                      </Button>
                    </Grid>
                    <PrimarniIdentifikatorTip />
                  </Box>
                </Grid>
              </Grid>
            </Container>
          </Box>
        )}
      {trenutniDatum && dayjs(trenutniDatum).isBefore("2023-10-01") && (
        <Container sx={{ mt: 2, textAlign: "center" }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card variant={"outlined"}>
                <CardContent>
                  <p>
                    Sustav za registraciju je dostupan od 01.10. tekuće godine.
                    (The registration system will be available from October 1st
                    of the current year)
                  </p>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      )}
      <Loading />
      <Poruka onClose={onDialogClose} />
    </FormProvider>
  );
};

export default Index;
